import React, { useState, useEffect } from "react";
import logoiii from "../assets/logoiii.png";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../redux/slices/authSlice";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Toast } from "../components/Toast";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [formErrors, setFormErrors] = useState({ email: "" });
  const [canSubmit, setCanSubmit] = useState(false);
  const [toast, setToast] = useState(null);
  const [rememberMe, setRememberMe] = useState(true); // Recordarme activado por defecto

  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  // Recuperar datos guardados
  useEffect(() => {
    const savedEmail = localStorage.getItem("savedEmail") || "";
    const savedPassword = localStorage.getItem("savedPassword") || "";

    setFormData({
      email: savedEmail,
      password: savedPassword
    });
  }, []);

  useEffect(() => {
    const newErrors = { email: "" };
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = "Por favor, ingresa un email válido";
    }

    setFormErrors(newErrors);
    setCanSubmit(
      formData.email.trim() !== "" &&
      validateEmail(formData.email) &&
      formData.password.trim() !== ""
    );
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      setToast({
        message: "Completa todos los campos correctamente",
        type: "error",
      });
      return;
    }

    try {
      const result = await dispatch(loginUser(formData));

      if (result.meta.requestStatus === "fulfilled") {
        if (rememberMe) {
          localStorage.setItem("savedEmail", formData.email);
          localStorage.setItem("savedPassword", formData.password);
        } else {
          localStorage.removeItem("savedEmail");
          localStorage.removeItem("savedPassword");
        }

        setToast({ message: "Inicio de sesión exitoso", type: "success" });

        // 🔑 Navegación post-login: prioridad ?next → nextAfterLogin → intendedMoviePath → fromState → /home
        setTimeout(() => {
          // 1) query ?next tiene prioridad absoluta
          const nextParam = new URLSearchParams(window.location.search).get('next');
          if (nextParam) {
            // limpieza de intenciones
            localStorage.removeItem('nextAfterLogin');
            localStorage.removeItem('intendedPlaybackPath');
            localStorage.removeItem('intendedPlayback');
            navigate(nextParam, { replace: true });
            return;
          }

          // 2) fallback: lo que guardó Anime (o Movie) explícitamente
          const nextAfterLogin = localStorage.getItem('nextAfterLogin');
          if (nextAfterLogin) {
            localStorage.removeItem('nextAfterLogin');
            // limpieza de legado por si existe
            localStorage.removeItem('intendedPlaybackPath');
            localStorage.removeItem('intendedPlayback');
            navigate(nextAfterLogin, { replace: true });
            return;
          }

          // 3) compat: ruta guardada vieja de películas (si la usabas antes)
          const intendedPath = localStorage.getItem("intendedMoviePath");
          if (intendedPath) {
            localStorage.removeItem("intendedMoviePath");
            navigate(intendedPath);
            return;
          }

          // 4) state.from (si llegaste por un PrivateRoute)
          const fromState = location.state?.from;
          if (fromState) {
            navigate(fromState);
            return;
          }

          // 5) default
          navigate('/home', { replace: true });
        }, 1500);
      }
    } catch (err) {
      setToast({
        message: error || "Error al iniciar sesión",
        type: "error",
      });
    }
  };

  return (
    <div
      className="bg-gray-900 text-white relative min-h-screen w-full flex items-center justify-center font-montserrat overflow-hidden py-12 sm:py-20 "
      style={{
        background: "linear-gradient(to bottom, #1a1a1a 0%, #0d0d0d 100%)",
      }}
    >
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0px, transparent 20px, #000000 20px, #000000 40px, transparent 40px, transparent 100px)`,
          backgroundSize: "100px 100%",
          boxShadow: "inset 0 0 10px rgba(0,0,0,0.8)",
        }}
      />
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0px, transparent 20px, rgba(0,0,0,0.5) 20px, rgba(0,0,0,0.5) 40px, transparent 40px, transparent 100px)`,
          backgroundSize: "100px 100%",
          filter: "blur(3px)",
          transform: "translateX(2px)",
        }}
      />

      <div
        className="p-4 sm:p-6 md:p-8 rounded-2xl w-full max-w-lg relative z-10   max-w-sm sm:max-w-md md:max-w-lg"
        style={{
          border: "2px solid rgba(255, 255, 255, 0.7)",
          boxShadow: "0 0 20px rgba(255, 255, 255, 0.2)",
          background: "rgba(0, 0, 0, 0)",
          backdropFilter: "blur(5px)",
        }}
      >
        <div className="flex items-center justify-center mb-6 max-w-sm sm:max-w-md md:max-w-lg">
          <img src={logoiii} alt="Logo" className="w-16 h-16 sm:w-24 sm:h-24 object-contain mr-2" />
          <span className="text-white font-bold text-lg sm:text-xl">EPIC MOVIE</span>
        </div>

        {error && !toast && (
          <div className="mb-4 p-2 bg-red-900 text-red-200 text-sm rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`w-full p-2 md:p-3 bg-transparent border ${formErrors.email ? 'border-red-500' : 'border-white/30'} rounded-md text-white`}
              required
            />
            {formErrors.email && (
              <p className="mt-1 text-red-500 text-xs flex items-center">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {formErrors.email}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">Contraseña</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full p-2 md:p-3 bg-transparent border border-white/30 rounded-md text-white"
              required
            />
          </div>

          <div className="flex items-center mt-2">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="mr-2"
            />
            <label className="text-sm text-white">Recordarme</label>
          </div>

          <button
            type="submit"
            disabled={loading || !canSubmit}
            className={`w-full mt-6 py-2 md:py-3 px-6 ${
              canSubmit ? 'bg-yellow-400 hover:bg-yellow-500' : 'bg-gray-500 cursor-not-allowed'
            } text-black font-medium rounded-full transition-all duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-yellow-300 focus:ring-opacity-50`}
          >
            {loading ? "Ingresando..." : "INGRESAR"}
          </button>
        </form>

        <p className="mt-4 text-center text-white">
          No tienes una cuenta? <Link to="/register" className="underline text-blue-500">Regístrate aquí</Link>
        </p>

        <p className="mt-2 text-center text-white">
          ¿Olvidaste tu contraseña? <Link to="/recover-password" className="underline text-blue-500">Recupérala aquí</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
